import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import { mainColor } from '../../../constants/theme'

export const CAMPAIGN_ACCENT_ORANGE = '#c2611a'
export const CAMPAIGN_ACCENT_BORDER = '#f97316'

const G = mainColor.hexa
const GL = 'rgba(0, 153, 115, 0.08)'

export { G, GL }

/**
 * Pixel gap between checkboxes / leading icons and label text (campaign modal).
 */
export const CAMPAIGN_ICON_TEXT_GAP_PX = 8

export const useCampaignModalSkeletonStyles = makeStyles((t: Theme) =>
	createStyles({
		overlay: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			outline: 'none',
		},
		dialogPaper: {
			width: 720,
			maxWidth: '95vw',
			maxHeight: '94vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			borderRadius: 10,
		},
		dialogPaperWide: {
			width: 950,
			maxWidth: '95vw',
			maxHeight: '94vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			borderRadius: 10,
		},
		header: {
			backgroundColor: G,
			color: '#fff',
			padding: '13px 20px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			flexShrink: 0,
		},
		stepperWrap: {
			borderBottom: '1px solid rgba(0,0,0,0.08)',
			flexShrink: 0,
		},
		body: {
			padding: t.spacing(2, 3, 3),
			overflowY: 'auto',
			flex: 1,
		},
		loadingState: {
			minHeight: 320,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: t.spacing(1.5),
		},
		footer: {
			padding: '10px 20px',
			borderTop: '1px solid rgba(0,0,0,0.08)',
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			backgroundColor: '#fafafa',
			flexShrink: 0,
		},
		rowGreen: {
			'&:hover': {
				backgroundColor: 'rgba(0, 153, 115, 0.05)',
			},
		},
		scopeTabPanel: {
			border: '1px solid rgba(0,0,0,0.15)',
			borderRadius: 10,
			overflow: 'hidden',
			marginBottom: t.spacing(2),
		},
		stickyCat: {
			padding: '5px 14px 4px',
			fontSize: 10.5,
			fontWeight: 600,
			color: '#9e9e9e',
			borderBottom: '1px solid rgba(0,0,0,0.06)',
			textTransform: 'uppercase',
			letterSpacing: '0.07em',
			backgroundColor: '#fafafa',
			position: 'sticky',
			top: 0,
			zIndex: 1,
		},
		scrollList: {
			overflowY: 'auto',
			maxHeight: 'min(180px, 26vh)',
			[t.breakpoints.up('sm')]: {
				maxHeight: 'min(210px, 28vh)',
			},
			[t.breakpoints.up('md')]: {
				maxHeight: 'min(240px, 32vh)',
			},
		},
		numSpinner: {
			display: 'flex',
			alignItems: 'center',
			border: '1px solid rgba(0,0,0,0.23)',
			borderRadius: 10,
			overflow: 'hidden',
			[t.breakpoints.down('xs')]: {
				width: '100%',
			},
		},
		numSpinnerBtn: {
			width: 34,
			height: 36,
			borderRadius: 0,
			backgroundColor: '#fafafa',
			padding: 0,
			[t.breakpoints.down('xs')]: {
				width: 30,
				height: 32,
			},
		},
		numSpinnerValue: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			minWidth: 54,
			flex: 1,
			padding: '4px 2px',
			gap: 2,
			[t.breakpoints.down('xs')]: {
				minWidth: 42,
			},
		},
		numSpinnerInput: {
			width: '100%',
			maxWidth: 80,
			border: 'none',
			background: 'transparent',
			textAlign: 'center',
			fontSize: 15,
			fontWeight: 600,
			outline: 'none',
			MozAppearance: 'textfield',
			'&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
				WebkitAppearance: 'none',
				margin: 0,
			},
			[t.breakpoints.down('xs')]: {
				fontSize: 13,
			},
		},
		namingCompactRow: {
			borderRadius: 10,
			padding: '16px',
			marginBottom: 16,
		},
		namingThumb: {
			width: 40,
			height: 40,
			borderRadius: 8,
			backgroundColor: '#eceff1',
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			cursor: 'pointer',
			flexShrink: 0,
		},
		namingAddImageBtn: {
			width: 40,
			height: 40,
			borderRadius: 8,
			border: '1px solid rgba(0, 153, 115, 0.5)',
			flexShrink: 0,
			padding: 0,
		},
		namingAddImageBtnMissing: {
			border: '1px solid #d32f2f',
			backgroundColor: 'rgba(211, 47, 47, 0.04)',
			color: '#d32f2f',
		},
		namingRequirementHint: {
			display: 'block',
			marginTop: 8,
			lineHeight: 1.4,
		},
		previewImageArea: {
			position: 'relative' as const,
			width: '100%',
			height: 220,
			backgroundColor: '#f1f4f7',
		},
		reviewPreviewCard: {
			borderRadius: 10,
			overflow: 'hidden',
			border: '1px solid rgba(0,0,0,0.12)',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'stretch',
		},
		reviewPreviewImage: {
			width: 96,
			minWidth: 96,
			maxWidth: 96,
			flexShrink: 0,
			alignSelf: 'stretch',
			minHeight: 96,
			backgroundColor: '#eceff1',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
			'& img': {
				width: '100%',
				height: '100%',
				objectFit: 'cover',
				display: 'block',
			},
		},
		reviewPreviewImagePlaceholder: {
			fontSize: 28,
			color: '#b0bec5',
		},
		reviewPreviewBody: {
			flex: 1,
			minWidth: 0,
			padding: t.spacing(1.5, 2),
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		reviewPreviewLabel: {
			fontSize: 11,
			fontWeight: 600,
			color: '#9e9e9e',
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
			marginBottom: t.spacing(0.75),
		},
		reviewPreviewTitle: {
			fontSize: 18,
			fontWeight: 700,
			color: '#212121',
			lineHeight: 1.25,
			marginBottom: t.spacing(0.5),
			wordBreak: 'break-word',
		},
		reviewPreviewSummary: {
			fontSize: 14,
			fontWeight: 500,
			color: G,
			lineHeight: 1.4,
		},
		reviewSectionCard: {
			flexGrow: 1,
			flexShrink: 0,
			flexBasis: 'auto' as const,
		},
		reviewEligibleTitle: {
			fontWeight: 600,
			color: '#212121',
			fontSize: 14,
			marginBottom: t.spacing(1),
		},
		reviewLayout: {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'flex-start',
			gap: t.spacing(2),
			width: '100%',
		},
		reviewLayoutLeft: {
			flex: '0 0 40%',
			width: '40%',
			maxWidth: '40%',
			minWidth: 0,
		},
		reviewLayoutRight: {
			flex: '1 1 60%',
			width: '60%',
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: t.spacing(1.5),
			'& > *': {
				marginBottom: 0,
			},
		},
	}),
)
